import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import { Sun, Moon, CheckCircle, Calendar, Bell, Users, Sparkles, ChevronRight } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    if (!loading && user) {
      navigate("/app", { replace: true });
    }
  }, [user, loading, navigate]);

  const getLoginUrl = () => {
    const redirectUrl = window.location.origin + "/app";
    // Use current origin for auth redirect — ensures same-origin on deployed site (fixes iOS)
    return `${window.location.origin}/api/auth/start?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/mr_chows_logo.png" alt="Mr. Chow's" className="h-12 object-contain" />
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-muted transition-colors"
              data-testid="theme-toggle"
            >
              {theme === "dark" ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className="pt-16">
        <section className="max-w-6xl mx-auto px-4 py-20 md:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
              <span className="font-light lowercase text-white" style={{ fontFamily: "'Georgia', serif" }}>simple list to</span><br />
              <span className="spark-text">SPARK YOU</span>
            </h1>

            <style>{`
              @keyframes sparkle {
                0%, 100% { opacity: 1; text-shadow: 0 0 4px #dc2626, 0 0 11px #dc2626, 0 0 19px #ff4444, 0 0 40px #ff4444; }
                25% { opacity: 0.9; text-shadow: 0 0 2px #dc2626, 0 0 8px #ff6b6b, 0 0 15px #ff4444, 0 0 30px #ff2222; }
                50% { opacity: 1; text-shadow: 0 0 7px #ff4444, 0 0 15px #dc2626, 0 0 25px #ff6b6b, 0 0 50px #ff4444, 0 0 80px #dc2626; }
                75% { opacity: 0.95; text-shadow: 0 0 3px #ff4444, 0 0 10px #dc2626, 0 0 20px #ff2222, 0 0 35px #ff4444; }
              }
              .spark-text {
                color: #dc2626;
                animation: sparkle 2s ease-in-out infinite;
                display: inline-block;
                position: relative;
                letter-spacing: 0.05em;
              }
              .spark-text::before,
              .spark-text::after {
                content: '✦';
                position: absolute;
                font-size: 0.35em;
                color: #ff6b6b;
                animation: float 1.5s ease-in-out infinite alternate;
              }
              .spark-text::before {
                top: -0.3em;
                left: -0.4em;
                animation-delay: 0s;
              }
              .spark-text::after {
                bottom: -0.1em;
                right: -0.5em;
                animation-delay: 0.7s;
              }
              @keyframes float {
                0% { transform: translateY(0) scale(0.8); opacity: 0.4; }
                100% { transform: translateY(-8px) scale(1.2); opacity: 1; }
              }
            `}</style>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Turn daily chaos into clear, shared to-dos
            </p>
            
            <a
              href={getLoginUrl()}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#dc2626] text-white rounded-full font-medium text-lg hover:bg-[#b91c1c] transition-colors shadow-lg shadow-[#dc2626]/25 no-underline"
              data-testid="hero-cta"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
              <ChevronRight className="w-5 h-5" />
            </a>
            
            <p className="text-sm text-muted-foreground mt-4">Happy To Keep You Organised</p>
          </div>
        </section>
      </main>
    </div>
  );
}
